import React, { useState, useEffect } from 'react';
import { getAllUsers, freezeUser, unfreezeUser, getBlacklist, addToBlacklist, removeFromBlacklist } from '../../services/api';
import toast from 'react-hot-toast';
import {
  UserCircleIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  LockOpenIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [newBlacklistAddress, setNewBlacklistAddress] = useState('');
  const [newBlacklistReason, setNewBlacklistReason] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, blacklistRes] = await Promise.all([getAllUsers(), getBlacklist()]);
      if (usersRes.data.success) setUsers(usersRes.data.users);
      if (blacklistRes.data.success) setBlacklist(blacklistRes.data.blacklist);
    } catch (error) {
      toast.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const handleFreeze = async (userId, currentStatus) => {
    try {
      if (currentStatus) {
        await unfreezeUser(userId);
        toast.success('User unfrozen');
      } else {
        await freezeUser(userId);
        toast.success('User frozen');
      }
      fetchData();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleAddToBlacklist = async () => {
    if (!newBlacklistAddress || !newBlacklistAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
      toast.error('Please enter a valid BSC address');
      return;
    }

    try {
      await addToBlacklist(newBlacklistAddress, newBlacklistReason);
      toast.success('Address added to blacklist');
      setShowBlacklistModal(false);
      setNewBlacklistAddress('');
      setNewBlacklistReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const handleRemoveFromBlacklist = async (address) => {
    try {
      await removeFromBlacklist(address);
      toast.success('Address removed from blacklist');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove address');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-gray-400">Loading user data...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head user-management-head">
        <div>
          <h1 className="dashboard-page-title">User Management</h1>
          <p className="dashboard-section-subtitle">Manage user accounts, access status, and wallet blacklist</p>
        </div>

        <button
          onClick={() => setShowBlacklistModal(true)}
          className="admin-action-btn"
          type="button"
        >
          <ShieldCheckIcon className="w-4 h-4" />
          Manage Blacklist
        </button>
      </div>

      <section className="card dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">All Users</h2>
          <p className="dashboard-section-subtitle">Platform users and account status overview</p>
        </div>

        <div className="dashboard-section-body">
          <div className="market-table-wrap">
            <table className="table-dark w-full">
              <thead>
                <tr>
                  <th>User</th>
                  <th>KYC Status</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-table-userbox">
                        <div className="user-avatar-wrap">
                          <UserCircleIcon className="w-8 h-8 text-slate-400" />
                        </div>
                        <div>
                          <p className="user-table-name">{user.email || user.phone}</p>
                          <p className="user-table-meta">ID: {user.id}</p>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span
                        className={
                          user.kyc_status === 'approved'
                            ? 'badge badge-success'
                            : user.kyc_status === 'pending'
                            ? 'badge badge-warning'
                            : 'badge badge-danger'
                        }
                      >
                        {user.kyc_status}
                      </span>
                    </td>

                    <td>
                      <span className="user-balance-value">
                        {parseFloat(user.balance_usdt || 0).toFixed(2)} USDT
                      </span>
                    </td>

                    <td>
                      {user.is_frozen ? (
                        <span className="badge badge-danger">Frozen</span>
                      ) : (
                        <span className="badge badge-success">Active</span>
                      )}
                    </td>

                    <td className="text-gray-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>

                    <td>
                      <button
                        onClick={() => handleFreeze(user.id, user.is_frozen)}
                        className={`user-freeze-btn ${user.is_frozen ? 'unfreeze' : 'freeze'}`}
                        type="button"
                      >
                        {user.is_frozen ? (
                          <>
                            <LockOpenIcon className="w-4 h-4" />
                            Unfreeze
                          </>
                        ) : (
                          <>
                            <LockClosedIcon className="w-4 h-4" />
                            Freeze
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showBlacklistModal && (
        <div className="kyc-modal-overlay">
          <div className="user-blacklist-modal">
            <div className="user-blacklist-top">
              <div>
                <h3 className="kyc-modal-title">Address Blacklist</h3>
                <p className="kyc-modal-subtitle">Manage restricted wallet addresses</p>
              </div>

              <button
                onClick={() => setShowBlacklistModal(false)}
                className="user-blacklist-close"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="user-blacklist-add-box">
              <h4 className="user-blacklist-section-title">Add to Blacklist</h4>

              <div className="user-blacklist-form">
                <input
                  type="text"
                  value={newBlacklistAddress}
                  onChange={(e) => setNewBlacklistAddress(e.target.value)}
                  className="auth-input"
                  placeholder="BSC Wallet Address (0x...)"
                />

                <input
                  type="text"
                  value={newBlacklistReason}
                  onChange={(e) => setNewBlacklistReason(e.target.value)}
                  className="auth-input"
                  placeholder="Reason (optional)"
                />

                <button
                  onClick={handleAddToBlacklist}
                  className="auth-submit-btn"
                  type="button"
                >
                  <PlusIcon className="w-4 h-4 inline mr-1" />
                  Add Address
                </button>
              </div>
            </div>

            <div className="user-blacklist-list-section">
              <h4 className="user-blacklist-section-title">Blacklisted Addresses</h4>

              {blacklist.length === 0 ? (
                <div className="styled-empty-state user-blacklist-empty">
                  <p className="styled-empty-title">No addresses in blacklist</p>
                  <p className="styled-empty-subtitle">Restricted wallet addresses will appear here</p>
                </div>
              ) : (
                <div className="user-blacklist-list">
                  {blacklist.map((item) => (
                    <div key={item.id} className="user-blacklist-item">
                      <div className="user-blacklist-item-copy">
                        <p className="user-blacklist-address">{item.address}</p>
                        {item.reason && <p className="user-blacklist-reason">{item.reason}</p>}
                      </div>

                      <button
                        onClick={() => handleRemoveFromBlacklist(item.address)}
                        className="user-blacklist-delete"
                        type="button"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;